import React from 'react';
import { Helmet } from 'react-helmet-async';

const LAST_UPDATED = 'July 25, 2026';
const CONTACT_EMAIL = 'info@docxsai.com';
const SUPPORT_EMAIL = 'support@docxsai.com';

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-2xl font-bold mb-3 text-[var(--color-text)]">{title}</h2>
    <div className="text-[var(--color-text-secondary)] text-base sm:text-lg space-y-3 text-justify">{children}</div>
  </div>
);

const PrivacyPolicyPage = () => (
  <div className="flex-1 flex flex-col public-page-glow relative overflow-hidden">
    <Helmet>
      <title>Privacy Policy – DocxsAi</title>
      <meta
        name="description"
        content="How DocxsAi collects, uses, stores, and protects personal data, uploaded documents, payment information, and AI processing in line with applicable privacy laws."
      />
      <link rel="canonical" href="https://docxsai.com/privacy" />
    </Helmet>

    <div className="absolute top-10 left-4 w-32 h-32 sm:top-20 sm:left-20 sm:w-80 sm:h-80 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute top-20 right-4 w-40 h-40 sm:top-40 sm:right-20 sm:w-96 sm:h-96 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute bottom-16 left-6 w-24 h-24 sm:bottom-32 sm:left-32 sm:w-64 sm:h-64 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute bottom-10 right-6 w-28 h-28 sm:bottom-20 sm:right-40 sm:w-72 sm:h-72 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl pointer-events-none" />

    <main className="relative z-10 flex-1 px-4 sm:px-6 pt-28 sm:pt-32 pb-10 sm:pb-12">
      <div className="max-w-6xl w-full mx-auto animate-slideUp">
        <h1 className="text-2xl sm:text-5xl font-bold mb-4 text-[var(--color-text)]">
          <span>Privacy</span>{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">Policy</span>
        </h1>

        <p className="mb-6 text-[var(--color-text-muted)] text-sm sm:text-base">
          Last updated: {LAST_UPDATED}
        </p>

        <p className="mb-10 text-[var(--color-text-secondary)] text-base sm:text-lg text-justify">
          DocxsAi (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) respects your privacy. DocxsAi is a product of TabsolTech,
          which owns and operates the Service. This Privacy Policy explains what information we collect, how we use it,
          who we share it with, how long we keep it, and the choices available to you when you use our website, web
          application, and related services (collectively, the &quot;Service&quot;).
        </p>

        <div className="text-left space-y-10">
          <Section title="1. Who This Policy Applies To">
            <p>
              This policy applies to visitors, registered users, paying subscribers, and anyone who uploads
              documents or uses features such as document analysis, revamp, generation, hiring shortlist,
              job board access, or SOP opportunities through DocxsAi.
            </p>
            <p>
              If you use the Service on behalf of a company, you confirm that you have authority to share
              information with us and to bind that organization to this policy.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p><strong className="text-[var(--color-text)]">Account and profile information</strong></p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Name, email address, password (stored in hashed form), and account preferences.</li>
              <li>Optional profile details such as company name, where required for certain features.</li>
              <li>Authentication, verification, and session data needed to keep you signed in securely.</li>
            </ul>

            <p><strong className="text-[var(--color-text)]">Documents and content you provide</strong></p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Contracts, CVs/resumes, statements of purpose (SOPs), and other files you upload.</li>
              <li>Text, prompts, job descriptions, form inputs, and metadata related to your documents.</li>
              <li>Generated, analyzed, or revamped outputs produced through the Service.</li>
              <li>Hiring shortlist uploads, including multiple CVs submitted for comparison against a job description.</li>
            </ul>

            <p><strong className="text-[var(--color-text)]">Payment and subscription information</strong></p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Subscription plan, billing cycle, payment status, transaction references, and purchase history.</li>
              <li>Web payments are processed by our payment provider. We do not store full card or bank details on our servers.</li>
            </ul>

            <p><strong className="text-[var(--color-text)]">Usage, technical, and security information</strong></p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Log data such as IP address, browser type, device information, pages visited, and feature usage.</li>
              <li>Error reports, performance data, and security events used to protect the platform.</li>
              <li>Communications you send to us, including support requests and contact form messages.</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="list-disc ml-6 space-y-2">
              <li>Provide document analysis, revamp, generation, reporting, and related AI-assisted features.</li>
              <li>Operate subscriptions, usage limits, hiring shortlist credits, and account management.</li>
              <li>Process payments, confirm transactions, and prevent fraud or abuse.</li>
              <li>Send service emails such as verification, password reset, reports, receipts, and account notices.</li>
              <li>Improve reliability, security, product quality, and customer support.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>
            <p>
              We process your information based on contractual necessity, legitimate interests in operating and
              securing the Service, your consent where required, and compliance with applicable law.
            </p>
          </Section>

          <Section title="4. AI Processing and Third-Party Service Providers">
            <p>
              DocxsAi uses artificial intelligence and cloud infrastructure to deliver the Service. To do this,
              we may share necessary data with trusted third-party providers, including:
            </p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong className="text-[var(--color-text)]">AI model providers</strong> (such as OpenAI and Anthropic) to analyze, generate, compare, and improve document content.</li>
              <li><strong className="text-[var(--color-text)]">Cloud storage providers</strong> (such as DigitalOcean Spaces) to store uploaded and generated files.</li>
              <li><strong className="text-[var(--color-text)]">Payment processors</strong> (such as Flutterwave) to handle web subscription and one-time purchases.</li>
              <li><strong className="text-[var(--color-text)]">Email delivery providers</strong> (such as Resend or Postmark) to send account and report emails.</li>
              <li><strong className="text-[var(--color-text)]">Hosting, analytics, and security vendors</strong> that help us run and protect the platform.</li>
            </ul>
            <p>
              When you upload a document, relevant content may be transmitted to AI providers for processing.
              We configure these services for operational use and do not sell your personal data. Providers are
              permitted to use your data only as needed to deliver services to us, subject to their own terms and
              security obligations.
            </p>
          </Section>

          <Section title="5. Sensitive and Confidential Information">
            <p>
              Documents you upload may contain personal, employment, financial, legal, immigration, or other
              sensitive information. You are responsible for ensuring you have the right to upload and process
              that content.
            </p>
            <p>
              Please do not upload information you are not authorized to share. If you are handling third-party
              personal data (for example, candidate CVs in hiring shortlist), you must have a lawful basis to do so
              and, where required, inform those individuals that their data will be processed through DocxsAi.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <p>
              We retain information for as long as reasonably necessary to provide the Service, meet legal
              obligations, resolve disputes, and enforce our agreements.
            </p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Account and subscription records are kept while your account remains active and for a limited period afterward where required by law or billing rules.</li>
              <li>Uploaded and generated documents are retained while linked to your account unless you delete them or close your account. When a document or account is deleted, we initiate deletion of associated files we identify in our configured cloud storage.</li>
              <li>Data export files prepared at your request are automatically removed from our systems after a limited period (currently 7 days).</li>
              <li>Security, payment, and operational logs may be retained for auditing, fraud prevention, legal obligations, or billing records.</li>
            </ul>
          </Section>

          <Section title="7. Payments">
            <p>
              Subscription and credit-pack purchases are processed by TabsolTech through Flutterwave for DocxsAi.
              Your statement may show TabsolTech as the merchant. Payment, security, and billing
              records may be retained where required for legal, fraud-prevention, or operational reasons. You can view
              your plan and payment history in your account.
            </p>
          </Section>

          <Section title="8. Data Security">
            <p>
              We use administrative, technical, and organizational safeguards designed to protect your
              information, including access controls, encrypted connections (HTTPS), hashed credentials, and
              restricted internal access.
            </p>
            <p>
              No online service can guarantee absolute security. You are responsible for keeping your login
              credentials confidential and using a strong, unique password.
            </p>
          </Section>

          <Section title="9. Your Rights and Choices">
            <p>Depending on applicable law, including Nigeria&apos;s data protection framework, you may have the right to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Access the personal information we hold about you.</li>
              <li>Request correction of inaccurate or incomplete information.</li>
              <li>Request deletion of your account and associated data, subject to legal or billing retention needs.</li>
              <li>Request a copy of your data through our data export feature.</li>
              <li>Withdraw consent where processing is based on consent, without affecting prior lawful processing.</li>
              <li>Object to or restrict certain processing, where applicable.</li>
              <li>Lodge a complaint with a relevant data protection authority.</li>
            </ul>
            <p>
              You can update profile details in your account settings, delete generated documents where available,
              request account deletion, or contact us to exercise your rights.
            </p>
          </Section>

          <Section title="10. Cookies and Similar Technologies">
            <p>
              We use cookies and similar technologies to keep you signed in, remember preferences, protect against
              abuse, and understand how the Service is used. You can control cookies through your browser settings,
              but some features may not work correctly if cookies are disabled.
            </p>
          </Section>

          <Section title="11. International Data Transfers">
            <p>
              Your information may be processed in Nigeria and in other countries where our service providers
              operate. Where data is transferred internationally, we take steps designed to ensure appropriate
              safeguards are in place consistent with applicable law.
            </p>
          </Section>

          <Section title="12. Children&apos;s Privacy">
            <p>
              DocxsAi is not intended for individuals under 18 years of age. We do not knowingly collect personal
              information from children. If you believe a child has provided us information, contact us and we will
              take appropriate steps to delete it.
            </p>
          </Section>

          <Section title="13. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we will post the
              updated policy on this page and revise the &quot;Last updated&quot; date. Continued use of the Service after
              changes become effective means you accept the updated policy.
            </p>
          </Section>

          <Section title="14. Contact Us">
            <p>
              For privacy questions, requests, or complaints, contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] underline">
                {CONTACT_EMAIL}
              </a>{' '}
              or{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] underline">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </Section>
        </div>
      </div>
    </main>
  </div>
);

export default PrivacyPolicyPage;
